import { Time } from '@angular/common';

import { RestTicketModel } from '@shared/interfaces/turnover.interface';
import { RestUnitSummaryModel } from '@shared/interfaces/unit.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { RestTemplateAreaModel, RestTemplateModel } from '@template/models/rest-template-model.interface';
import { ResponsiblePartyType } from '@ui-components/modals/add-ticket-modal/add-ticket-modal.component';

import { InspectionHasIssues, InspectionQueryType } from '../components/inspections-list/constants';

export interface RestInspectionCreateModel {
  inspectionTemplateId: number;
  assignee: number;
  dueDate: Date;
  notifyAssignee: boolean;
  recurrencePeriod?: InspectionRecurrencePeriod;
  createOrNotifyOn?: Date;
  autoExpiredOn?: Date;
  endDate?: Date;
  propertyId?: number;
  unitId?: number;
  bulkUnitIds?: number[];
  notifyWhenDone?: boolean;
  notifyWhenDoneUserIds?: string;
  notifyWhenNonDefaultOrNASelectedUserIds?: string;
  isResident?: boolean;
  reviewRequired?: number;
  notifyWhenReviewRequired?: boolean;
  reviewRequiredByUserId?: number;
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentTime?: Date;
  appointmentDate?: Date;
  appointmentNotes?: string;
  notifyResidentWhenDone?: boolean;
  diligenceId?: number;
  notifyAgainOn?: Date;
  notifyAgainAfterNDays?: number;
  notifyAgainStopAfterNTimes?: number | null;
  lastTimeAssigneeNotifiedOn?: Date;
  notifyAgainAssigneeNextTimeDate?: Date;
  notifiedAgainTimes?: number;
  inspectionMobileMetadata?: InspectionMobileMetadataModel;
}

export interface RestInspectionUpdateModel extends RestInspectionCreateModel {
  id: number;
  completedBy: number;
  modifiedAt: Date;
  completedAt: Date;
  createdAt: Date;
  status: number;
  turnStepScheduleId?: number;
  calendarPosition: number;
  inspectionTemplate?: RestTemplateModel;
  updatedFields?: string[];
  inspectionMobileMetadata?: InspectionMobileMetadataModel;
}

export interface InspectionMobileMetadataModel {
  mobileAppVersion?: string;
  filesAwaitingUpload?: number;
  userId: number;
  lastTimeUpdatedUtc: string;
}

export interface InspectionMobileMetadataUpdateRequest {
  filesAwaitingUpload?: number;
}

export enum ScoringModels {
  None = 0,
  Maximum100 = 1,
}

export enum ScoreWeightModes {
  Automatic,
  Manual,
}

export enum InspectionRecurrencePeriod {
  DoNotRepeat = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Quarterly = 4,
  SemiAnnually = 5,
  Annually = 6,
  SemiMonthly = 7,
  SameWeekMonthly = 8,
  SameWeekQuarterly = 9,
  EveryTwoYears = 10,
  EveryThreeYears = 11,
  EveryFourYears = 12,
  EveryFiveYears = 13,
  EveryTenYears = 14,
  MonWedFri = 15,
  TueTh = 16,
  MonFri = 17,
}

export interface BulkInspectionsRequestParams {
  queryType?: InspectionQueryType;
  startDate?: Date;
  endDate?: Date;
}

export interface RestInspectionsModel {
  id: number;
  inspectionTemplateId: number;
  inspectionTemplate?: RestTemplateModel;
  unitId: number;
  completedBy: number;
  unit: RestUnitSummaryModel;
  assignee: number;
  assigneeUser: RestUserModel;
  status: InspectionStatus;
  recurrencePeriod?: InspectionRecurrencePeriod;
  inspectionContents?: RestInspectionContents[];
  completedAt: Date;
  modifiedAt: Date;
  createdAt: Date;
  dueDate: Date;
  calendarPosition: number;
  turnStepScheduleId?: number;
  inspectionType: InspectionType;
  isTurboMode?: boolean;
  attachments?: {
    attachment?: RestTypedInspectionAttachment;
    autoScopeReport?: RestTypedInspectionAttachment;
    none: RestTypedInspectionAttachment[];
    nSpireReport: RestTypedInspectionAttachment;
  };
  totalRooms?: number;
  completedRooms?: number;
  notifyAssignee: boolean;
  createOrNotifyOn?: Date;
  autoExpiredOn?: Date;
  endDate?: Date;
  parentInspectionId?: number;
  parentInspection?: ParentInspectionModel;
  dynamicInspectionId?: number;
  propertyId?: number;
  bulkUnitIds?: number[];
  isBulk: boolean;
  notifyWhenDone?: boolean;
  notifyWhenDoneUserIds?: string;
  notifyWhenNonDefaultOrNASelectedUserIds?: string;
  scoringModel?: ScoringModels;
  resultScore?: number;
  inspectionStartedOn?: Date;
  inspectionFinishedOn?: Date;
  isResident?: boolean;
  isArchived: boolean;
  reviewRequired: number;
  reviewRequiredByUserId: number;
  notifyWhenReviewRequired: boolean;
  reviewCompletedBy: number;
  reviewCompletedOn: Date;
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentTime?: Date;
  appointmentDate?: Date;
  appointmentNotes?: string;
  notifyResidentWhenDone?: boolean;
  updatedFields?: string[];
  totalInspectionTimeInSeconds: number;

  uncompletedChildInspectionMinDueDate?: Date;
  bulkProcessingCompleted?: boolean;
  uncompletedChildInspections?: number;
  completedChildInspections?: number;
  lateChildInspections?: number;
  progress?: number;
  lastRecurringInstance?: RestInspectionsModel;
  createdBy?: number;
  modifiedBy?: number;
  recurringBulkHolder?: boolean;
  childInspectionsTotalAssignees?: number;
  childInspectionsLastDueDate?: Date | null;

  notifyAgainOn?: Date;
  lastTimeAssigneeNotifiedOn?: Date;
  notifyAgainAssigneeNextTimeDate?: Date;
  notifyAgainAfterNDays?: number;
  notifyAgainStopAfterNTimes?: number;
  notifiedAgainTimes?: number;
  hasNonDefaultOrNAAnswersLeft?: InspectionHasIssues;
  totalNonDefaultOrNAAnswersLeft?: number;

  inspectionMobileMetadata?: InspectionMobileMetadataModel;
  diligenceId?: number;
  isDeleted?: boolean;
}

export interface ParentInspectionModel {
  bulkUnitIds: number[];
  createdBy: number;
  dueDate: Date;
  endDate: Date;
  isBulk: boolean;
  recurrencePeriod: InspectionRecurrencePeriod;
}

export enum DynamicSchedule {
  Notice = 1,
  MoveOut = 2,
  MoveIn = 3,
  MakeReady = 4,
}

export enum DynamicInspectionAssigneeSelectionMode {
  ManualEntry = 0,
  DefaultAssignee = 1,
  PropertyManager = 2,
  Resident = 3,
}

export enum DynamicPropertyType {
  None = 0,
  Portfolio = 1,
  Property = 2,
  Unit = 4,
}

export enum InspectionAttachmentType {
  None,
  Attachment,
  AutoScopeReport,
  NSpireReport,
  DailySummary,
  EmptyForm,
}

export interface RestDynamicInspectionUpdateModel {
  id?: number;
  inspectionTemplateId: number;
  dynamicallyScheduledOn: DynamicSchedule;
  propertyOrPortfolioId: number;
  propertyType: DynamicPropertyType;
  createOrNotifyOn: number;
  autoExpiredAfterDueDateDays: number;
  notifyAssignee: boolean;
  //doNotSetAssignee: boolean;
  dueDate: number;
  assignee?: number;
  endDate: string | Date;
  notifyWhenDone?: boolean;
  notifyWhenDoneUserIds?: string;
  notifyWhenNonDefaultOrNASelectedUserIds?: string;
  //assignToResident?: boolean;
  propertyIds?: number[] | null;
  reviewRequired: number;
  notifyWhenReviewRequired: boolean;
  reviewRequiredByUserId: number;
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentTime?: Date;
  appointmentDate?: number;
  appointmentNotes?: string;
  //notifyResidentWhenDone?: boolean;
  //setReviewRequiredByUserIdWithDefaultAssignee?: boolean;
  //addDefaultAssigneeToNotifyWhenDone?: boolean;
  addDynamicNotifyWhenDone: DynamicInspectionAssigneeSelectionMode[];
  reviewRequiredByUserIdAssigneeMode: DynamicInspectionAssigneeSelectionMode;
  assigneeDynamicAssigneeMode: DynamicInspectionAssigneeSelectionMode;

  notifyAgainOn?: number;
  lastTimeAssigneeNotifiedOn?: Date;
  notifyAgainAssigneeNextTimeDate?: Date;
  notifyAgainAfterNDays?: number;
  notifyAgainStopAfterNTimes?: number;
  notifiedAgainTimes?: number;
}

export interface RestDynamicInspectionModel {
  id: number;
  inspectionTemplateId: number;
  //doNotSetAssignee: boolean;
  dynamicallyScheduledOn: DynamicSchedule;
  createOrNotifyOn: number;
  autoExpiredAfterDueDateDays: number;
  notifyAssignee: boolean;
  dueDate: number;
  endDate: string | Date;
  actualAssigneeId: number;
  propertyOrPortfolioId: number;
  propertyId: number;
  propertyIds: number[];
  portfolioId: number;
  propertyType: DynamicPropertyType;
  assignee: number;
  notifyWhenDone?: boolean;
  notifyWhenDoneUserIds?: string;
  notifyWhenNonDefaultOrNASelectedUserIds?: string;
  //assignToResident?: boolean;
  reviewRequired: number;
  notifyWhenReviewRequired: boolean;
  reviewRequiredByUserId: number;
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentTime?: Date;
  appointmentDate?: number;
  appointmentNotes?: string;
  //notifyResidentWhenDone?: boolean;
  addDynamicNotifyWhenDone: DynamicInspectionAssigneeSelectionMode[];
  reviewRequiredByUserIdAssigneeMode: DynamicInspectionAssigneeSelectionMode;
  assigneeDynamicAssigneeMode: DynamicInspectionAssigneeSelectionMode;
  isHidden?: boolean;
  isParent?: boolean;
  allProperties?: boolean;

  notifyAgainOn?: number;
  lastTimeAssigneeNotifiedOn?: Date;
  notifyAgainAssigneeNextTimeDate?: Date;
  notifyAgainAfterNDays?: number;
  notifyAgainStopAfterNTimes?: number;
  notifiedAgainTimes?: number;
}

export interface RestTypedInspectionAttachment {
  id: number;
  dateCreated: string;
  dateUpdated: string;
  isActive: boolean;
  fileUploadId: number;
  internalName: string;
  originalFileName: string;
  size: number;
  url: string;
  attachmentTypeUrn: string;
  title: string;
  description: string;
  externalUrl: string;
  inspectionAttachmentType: InspectionAttachmentType;
  inspectionTemplateName?: string;
  trueType: number;
}

// BE Domain.Models.Entities.InspectionContent
export interface RestInspectionContents {
  id: number;
  inspectionId: number;
  inspectionTemplateAreaId: number | null;
  sharedInspectionLibrarySpaceId: number | null;
  title?: string;
  surveyJSON?: string;
  answerJSON?: string;

  itemsCost?: string;
  areaSubTotal?: number;
  areaTime?: number;
  areaLaborCost?: number;
  areaMaterialsCost?: number;

  autoScopeParseStatus?: string;
  status: InspectionItemStatus;

  createTicketFlag: boolean;
  responsibleParty?: ResponsiblePartyType;
  ticketDetailedInformation?: string;

  imageCount?: number;
  isManualItem?: boolean;
  cost?: number;
  position?: number;

  inspectionTemplateArea?: RestTemplateAreaModel;
  sharedInspectionLibrarySpace?: any;

  completedAt?: Date;
  completedBy?: number;
  completedFrom?: string;

  createdAt: Date;
  createdBy: number;
  modifiedAt: Date;
  modifiedBy?: number;

  isDeleted: boolean;
  deletedAt?: Date;
  deletedBy?: number;

  inspectionStartedOn?: string;
  inspectionFinishedOn?: string;
  resultScore?: number;

  scoreWeightMode?: ScoreWeightModes;
  scoreWeight?: number;
  itemsScore?: string;
  itemsScoreParseStatus?: string;

  totalInspectionTimeInSeconds?: number;
  contentType?: AreaContentType;

  completedByUser?: RestUserModel;
  createdByUser?: RestUserModel;
  modifiedByUser?: RestUserModel;
  deletedByUser?: RestUserModel;

  inspection?: any;
  tickets?: RestTicketModel[] | null;

  inspectionTemplateAreaTitle?: string;
  sharedInspectionLibrarySpaceTitle?: string;

  surveyPageId?: number;
  surveyResult?: SurveyJsModel;
}

export interface SurveyJsModel {
  pages?: Page[] | null;
  surveyId?: string | null;
  surveyPostId?: string | null;
  cookieName?: string | null;
  sendResultOnPageNext?: boolean | null;
  surveyShowDataSaving?: boolean | null;
  focusFirstQuestionAutomatic?: boolean | null;
  focusOnFirstError?: boolean | null;
  showNavigationButtons?: number | null;
  showPrevButton?: boolean | null;
  showTitle?: boolean | null;
  showPageTitles?: boolean | null;
  showCompletedPage?: boolean | null;
  requiredText?: string | null;
  questionStartIndex?: string | null;
  storeOthersAsComment?: boolean | null;
  maxTextLength?: number | null;
  maxOthersLength?: number | null;
  goNextPageAutomatic?: boolean | null;
  title?: any | null;
  description?: any | null;
  completedHtml?: string | null;
  completedBeforeHtml?: string | null;
  loadingHtml?: string | null;
  startSurveyText?: string | null;
  pagePrevText?: string | null;
  pageNextText?: string | null;
  completeText?: string | null;
  questionTitleTemplate?: string | null;
  showPageNumbers?: boolean | null;
  isSinglePage?: boolean | null;
  firstPageIsStarted?: boolean | null;
  maxTimeToFinish?: number | null;
  maxTimeToFinishPage?: number | null;
  instructions?: string | null;
  showInstructions?: boolean | null;
  showSpareParts?: boolean | null;
  sparePartsGroups?: number[] | null;
}

export interface Page {
  navigationButtonsVisibility?: PageNavigationButtonsVisibility;
  questionsOrder?: PageQuestionsOrder;
  maxTimeToFinish?: number | null;
  pageType?: string | null;

  laborCost?: number | null;
  materials?: number | null;
  timeCost?: number | null;

  instructions?: string | null;
  scoreWeightMode?: ScoreWeightModes;
  scoreWeight?: number | null;
  showInstructions?: boolean | null;

  overrideSparePartsSettings?: boolean | null;
  sparePartsGroups?: number[] | null;
  showSpareParts?: boolean | null;
  SelectedSpareParts?: ItemSparePart[] | null;

  hideFileTicket?: boolean | null;

  title?: string | null;
  compulsoryItems?: string[] | null;
  description?: any | null;

  visibleIf?: string | null;
  elements?: ElementBase[] | null;

  questionTitleLocation?: PanelbaseQuestionTitleLocation;
  visible?: boolean | null;
  enableIf?: string | null;

  mainImage?: ImageModel[] | null;

  name?: string | null;
  type?: ElementType;
  guid?: string | null;
  value?: string | null;
  customFieldBinding?: string | null;
  isRequired?: boolean | null;
  defaultValue?: string | null;

  displayMode?: CiteElementDisplayMode;
}

export interface ImageModel {
  fileUploadId?: number | null;
  cloudUri?: string | null;
  extension?: string | null;
  base64Data?: string | null;
  itemTitle?: string | null;
  src?: string | null; // BE readonly
  imageOrientation?: ImageOrientation;
  imageSize: number;
  isVideo: boolean;
  hasThumbnail: boolean;
  applyCustomDimensions: boolean;
}

export enum ImageOrientation {
  NA = 0,
  PortraitUp = 1,
  LandscapeRight = 2,
}

export interface ElementBase {
  name?: string | null;
  type: ElementType;
  guid?: string | null;
  value?: string | null;
  customFieldBinding?: string | null;
  isRequired?: boolean | null;
  defaultValue?: string | null;
  displayMode?: CiteElementDisplayMode;
  visible?: boolean | null;
  imageCaptureMode?: ImageCaptureMode | null;
}

export enum AreaContentType {
  Standard = 0,
  PostCompleteSurvey = 1,
}

export enum CiteElementDisplayMode {
  Value = 0,
  Variance = 1,
}

export enum ImageCaptureMode {
  PhotoAndGallery = 0,
  PhotoOnly = 1,
  AnyFile = 2,
}

export type ElementType =
  | 'page'
  | 'panel'
  | 'checkbox'
  | 'radiogroup'
  | 'dropdown'
  | 'text'
  | 'comment'
  | 'boolean'
  | 'file'
  | 'propertystate'
  | 'quantity'
  | 'hours'
  | 'money'
  | 'number'
  | 'int'
  | 'date'
  | 'datetime'
  | 'user';

export enum PageNavigationButtonsVisibility {
  Inherit = 0,
  Show = 1,
  Hide = 2,
}

export enum PageQuestionsOrder {
  Default = 0,
  Initial = 1,
  Random = 2,
}

export enum PanelbaseQuestionTitleLocation {
  Default = 0,
  Top = 1,
  Bottom = 2,
  Left = 3,
  Hidden = 4,
}

export interface ItemSparePart {
  sparePartId?: number | null;
  name?: string | null;
  quantity?: number | null;
  responsibility?: number | null;
  comment?: string | null;
  totalCost?: number | null;
}

export interface SendInspectionReportRequest {
  to: string[];
  subject: string;
  body: string;
}

export enum InspectionStatus {
  NotStarted,
  InProgress,
  Completed,
  Canceled,
  Expired,
  Review,
  Late = 99,
}

export enum InspectionByStatusFilter {
  TodayDue,
  UpComing,
  IncompleteAndLate24h,
  IncompleteLate48h,
  IncompleteLate72hPlus,
  CompletedOnTime,
  Completed24hLate,
  Completed48hLate,
  Completed72hLatePlus,
  Review,
  ReviewLate,
  Completed,
  NonCompleted,
  CompletedLate,
  Late,
  NotStartedLate,
  NotStarted,
  InProgress,
  InProgressLate,
  Canceled,
  Expired,
  TodayDueCompleted,
  Total,
}

export const InspectionByStatusFilterLabels: Record<InspectionByStatusFilter, string> = {
  [InspectionByStatusFilter.TodayDue]: 'Due today',
  [InspectionByStatusFilter.UpComing]: 'Upcoming',
  [InspectionByStatusFilter.IncompleteAndLate24h]: 'Incomplete (24 hours overdue)',
  [InspectionByStatusFilter.IncompleteLate48h]: 'Incomplete (48 hours overdue)',
  [InspectionByStatusFilter.IncompleteLate72hPlus]: 'Incomplete (over 72 hours overdue)',
  [InspectionByStatusFilter.CompletedOnTime]: 'Completed on time',
  [InspectionByStatusFilter.Completed24hLate]: 'Completed (24 hours late)',
  [InspectionByStatusFilter.Completed48hLate]: 'Completed (48 hours late)',
  [InspectionByStatusFilter.Completed72hLatePlus]: 'Completed (over 72 hours late)',
  [InspectionByStatusFilter.Review]: 'Review',
  [InspectionByStatusFilter.ReviewLate]: 'Review (late)',
  [InspectionByStatusFilter.Completed]: 'Completed',
  [InspectionByStatusFilter.NonCompleted]: 'Incomplete',
  [InspectionByStatusFilter.CompletedLate]: 'Completed (late)',
  [InspectionByStatusFilter.Late]: 'Late',
  [InspectionByStatusFilter.NotStartedLate]: 'Not Started (late)',
  [InspectionByStatusFilter.NotStarted]: 'Not Started',
  [InspectionByStatusFilter.InProgress]: 'In Progress',
  [InspectionByStatusFilter.InProgressLate]: 'In Progress (late)',
  [InspectionByStatusFilter.Canceled]: 'Canceled',
  [InspectionByStatusFilter.Expired]: 'Expired',
  [InspectionByStatusFilter.TodayDueCompleted]: 'Due today (completed)',
  [InspectionByStatusFilter.Total]: 'Total',
};

export enum InspectionItemStatus {
  Incompleted,
  Skipped,
  Completed,
}

export enum InspectionType {
  MoveOutVacant,
  Annual,
  ServiceCheck,
  MoveIn,
  CustomInspection,
  Periodic,
  MoveOutOccupied,
  Diligence,
}

export enum PropertyInspectionType {
  Dynamic,
  Repeating,
  OneTime,
}

export enum InspectionScheduleType {
  None,
  Standard,
  Resident,
  Repeating,
  DynamicNotice,
  DynamicMoveIn,
  DynamicMoveOut,
  DynamicMakeReady,
}

export enum ReviewRequiredPolicy {
  NotRequired,
  RequireIfNonDefaultRatingOrNASelected,
  Always,
  RequireIfNonDefaultRatingSelected,
  RequireIfNonDefaultRatingSelectedOrCommentLeft,
  NotifyIfNonDefaultRatingOrNALeft,
}

export interface InspectionDelegateReqI {
  newAssignee: number;
  notify: boolean;
}

export interface InspectionAppointment {
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentDate?: Date;
  appointmentTime?: Time;
  appointmentNotes?: string;
}

export interface InspectionCheckForDuplicateReqModel {
  inspectionTemplateId: number;
  dueDate: string | Date;
  unitIds: number[];
  isResident: boolean;
}
